import { useState, useRef, useEffect } from 'react';
import { Bell, CheckCheck, Network, UserPlus, MessageCircle, ListTodo, AtSign, Clock, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useNotificaciones, type Notificacion } from '@/hooks/useNotificaciones';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

const TIPO_CONFIG: Record<Notificacion['tipo'], { icon: typeof Bell; color: string; ruta: string }> = {
  SOLICITUD_RED:     { icon: UserPlus,       color: 'text-blue-500',   ruta: '/red?tab=contactos' },
  REFERIDO_NUEVO:    { icon: Network,        color: 'text-violet-500', ruta: '/referidos' },
  REFERIDO_ACEPTADO: { icon: Network,        color: 'text-green-500',  ruta: '/referidos' },
  REFERIDO_RECHAZADO:{ icon: Network,        color: 'text-red-500',    ruta: '/referidos' },
  CHAT_MENSAJE:      { icon: MessageCircle,  color: 'text-primary-500',ruta: '/red' },
  TAREA_ASIGNADA:    { icon: ListTodo,       color: 'text-cyan-600',   ruta: '/tareas' },
  TAREA_MENCION:     { icon: AtSign,         color: 'text-violet-500', ruta: '/tareas' },
  TAREA_POR_VENCER:  { icon: Clock,          color: 'text-amber-500',  ruta: '/tareas' },
  TAREA_VENCIDA:     { icon: AlertTriangle,  color: 'text-red-500',    ruta: '/tareas' },
};

interface Props {
  onAbrirChat?: (medicoId: string) => void;
}

export function NotificacionesBell({ onAbrirChat }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { notificaciones, noLeidas, marcarLeida, marcarTodasLeidas } = useNotificaciones();

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  async function handleClick(n: Notificacion) {
    if (!n.leida) await marcarLeida(n.id);
    setOpen(false);

    if (n.tipo === 'CHAT_MENSAJE' && n.metadatos?.medicoId && onAbrirChat) {
      onAbrirChat(n.metadatos.medicoId);
    } else if (n.tipo.startsWith('TAREA_') && n.metadatos?.tareaId) {
      // Abre el detalle de la tarea directamente.
      navigate(`/tareas?t=${n.metadatos.tareaId}`);
    } else {
      navigate(TIPO_CONFIG[n.tipo].ruta);
    }
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors text-text-secondary"
      >
        <Bell size={20} />
        {noLeidas > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
            {noLeidas > 9 ? '9+' : noLeidas}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-2xl shadow-xl border border-gray-100 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
            <span className="text-sm font-semibold text-text-primary">
              Notificaciones {noLeidas > 0 && <span className="text-red-500">({noLeidas})</span>}
            </span>
            {noLeidas > 0 && (
              <button
                onClick={marcarTodasLeidas}
                className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium"
              >
                <CheckCheck size={13} /> Marcar todas
              </button>
            )}
          </div>

          {/* Lista */}
          <div className="max-h-96 overflow-y-auto divide-y divide-gray-50">
            {notificaciones.length === 0 ? (
              <div className="py-10 text-center text-sm text-text-secondary">
                Sin notificaciones
              </div>
            ) : (
              notificaciones.map(n => {
                const cfg = TIPO_CONFIG[n.tipo];
                const Icon = cfg.icon;
                return (
                  <button
                    key={n.id}
                    onClick={() => handleClick(n)}
                    className={cn(
                      'w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-gray-50 transition-colors',
                      !n.leida && 'bg-primary-50/50',
                    )}
                  >
                    <div className={cn('mt-0.5 flex-shrink-0', cfg.color)}>
                      <Icon size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={cn('text-xs font-semibold text-text-primary', !n.leida && 'font-bold')}>
                        {n.titulo}
                      </p>
                      <p className="text-xs text-text-secondary mt-0.5 line-clamp-2">{n.cuerpo}</p>
                      <p className="text-[10px] text-gray-400 mt-1">
                        {formatDistanceToNow(new Date(n.createdAt), { addSuffix: true, locale: es })}
                      </p>
                    </div>
                    {!n.leida && (
                      <div className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0 mt-1" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
